import { Icon, Menu, MenuItem, Position, Spinner } from "@blueprintjs/core"
import { DateRangeInput } from "@blueprintjs/datetime"
import CustomButton from "components/Atoms/CustomButton"
import CustomPopover from "components/Atoms/CustomPopover"
import CustomTag from "components/Atoms/CustomTag"
import CustomReactTable from "components/Templates/CustomReactTable"
import dayjs from "dayjs"
import { useHost } from "global/host.global"
import { useOutstandingUserDetailQuery } from "generated/graphql"
import OrderDetail from "pages/order/OrderDetail"
import { COLUMNS } from "pages/order/OrderTable"
import OrderActions from "pages/order/table/OrderActions"
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import useAxios from "services/axios.service"
import { useResetSelected, useSelected } from "store/selector.redux"
import AppToaster from "utils/AppToaster"
import { inr } from "utils/formatters.utils"
import MultipleOrderAddTransaction from "./MultipleOrderAddTranscation"

const SHOW_DATE = "DD MMM YYYY"
const API_DATE = "YYYY-MM-DD"
type Range = [Date | null, Date | null]

const OUTSTANDING_COLUMNS = COLUMNS.filter(
  c => c.Header !== "Vendor" && c.Header !== "Business Name"
).map((c: any) =>
  !c.Header && !c.accessor && c.Cell
    ? {
        ...c,
        Cell: ({ original }) => (
          <OrderActions order={original} onlyTransaction />
        ),
      }
    : c
)

const SummaryCard = ({ label, value, sub, tone, icon }: any) => (
  <div className="rounded-lg border p-4 bg-white">
    <div className="flex justify-between items-start">
      <span className="text-xs text-gray-600">{label}</span>
      <Icon icon={icon} className="text-gray-600" />
    </div>
    <div className={`text-2xl font-600 my-2 ${tone || ""}`}>{value}</div>
    <div className="text-xs text-gray-600">{sub}</div>
  </div>
)

const VendorOutstandingOrders = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [range, setRange] = useState<Range>([null, null])
  const [settleOpen, setSettleOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const axios = useAxios()
  const { subdomain } = useHost()
  const selected = useSelected("orders")
  const resetSelected = useResetSelected("orders")

  // The "orders" selection is shared with the Orders screen, so start and end clean
  useEffect(() => {
    resetSelected()
    return resetSelected
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data, loading, error, refetch } = useOutstandingUserDetailQuery({
    variables: {
      userId: id,
      filter: {
        date_Gte: range[0] ? dayjs(range[0]).format(API_DATE) : undefined,
        date_Lte: range[1] ? dayjs(range[1]).format(API_DATE) : undefined,
      },
    },
    skip: !id,
  })
  const detail = data?.outstandingUserDetail
  const user: any = detail?.user
  // Vendor types are the user groups under the retail (RTL) category
  const vendorTypes = (user?.groups ?? []).filter(
    g => g?.category?.codename === "RTL"
  )
  const shops = (detail?.shops ?? []).filter(Boolean)

  const orders = useMemo(() => (detail?.orders ?? []).filter(Boolean), [detail])

  const vendorName = [user?.firstName, user?.lastName].filter(Boolean).join(" ")
  // Prefer the vendor's own address, fall back to the first shop's
  const ua = user?.address
  const addr =
    ua?.village || ua?.address || ua?.district ? ua : shops[0]?.address
  const shopAddress = [
    addr?.village,
    addr?.address,
    addr?.district,
    addr?.pincode,
  ]
    .filter(Boolean)
    .join(", ")

  const picked = orders.filter(o => selected?.[o.id])
  const hasSelection = picked.length > 0
  // Server totals by default, recomputed over the checked rows once any is checked

  async function downloadExcel() {
    setDownloading(true)
    try {
      const res = await axios.get(
        `/services/accounts/outstanding-users/${id}/excel/`,
        {
          params: {
            date__gte: range[0] ? dayjs(range[0]).format(API_DATE) : undefined,
            date__lte: range[1] ? dayjs(range[1]).format(API_DATE) : undefined,
          },
          responseType: "blob",
          headers: { tenant: subdomain },
        }
      )
      const url = URL.createObjectURL(res.data)
      const a = document.createElement("a")
      a.href = url
      a.download = `outstanding_user_${id}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      AppToaster.show({
        message: e?.message || "Failed to download outstanding excel",
        intent: "danger",
      })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="vendor-outstanding">
      {/* Order links set ?id= on this page, so host the detail dialog here */}
      <OrderDetail />
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b">
        <CustomButton icon="arrow-left" minimal onClick={() => navigate(-1)} />
        <div className="flex-1">
          <h3 className="m-0 font-600">Outstanding Order Details</h3>
          <span className="text-xs text-gray-600">Vendor summary</span>
        </div>
        <DateRangeInput
          allowSingleDayRange
          formatDate={d => dayjs(d).format(SHOW_DATE)}
          parseDate={str => dayjs(str, SHOW_DATE).toDate()}
          value={range}
          onChange={r => setRange([r[0] || null, r[1] || null])}
          startInputProps={{ placeholder: "From" }}
          endInputProps={{ placeholder: "To" }}
          popoverProps={{ minimal: true }}
        />
        <CustomButton
          icon="th"
          text="Download Excel"
          loading={downloading}
          disabled={!id}
          onClick={downloadExcel}
        />
      </div>

      {loading && !detail ? (
        <div className="py-10">
          <Spinner />
        </div>
      ) : error || !detail ? (
        <div className="m-8 bg-white border rounded-lg p-6 text-center text-red-500">
          {error?.message || "Outstanding details not available"}
        </div>
      ) : (
        <div className="p-4 space-y-4 mx-8 my-4">
          <div
            className="bg-white border rounded-lg p-4"
            style={{ border: "1px solid #E5E7EB" }}
          >
            <div className="border-b pb-3 mb-4">
              <h3 className="m-0 font-600">
                Business Name - {shops[0]?.name || vendorName}
              </h3>
              <span className="text-xs text-gray-600">
                {shopAddress || "-"}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs font-600 text-gray-600 mb-2">
                  VENDOR DETAILS
                </div>
                {[
                  ["Phone No", user?.number],
                  ["POC Name", vendorName],
                ].map(([k, v]) => (
                  <div key={k} className="flex text-sm mb-1">
                    <span className="w-24 text-gray-600">{k}</span>
                    <span>{v || "-"}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-600 text-gray-600 mb-2">
                  VENDOR TYPES
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {vendorTypes.length
                    ? vendorTypes.map(t => (
                        <CustomTag key={t.id} minimal>
                          {t.name}
                        </CustomTag>
                      ))
                    : "-"}
                </div>
                <div className="text-xs font-600 text-gray-600 mb-2">SHOPS</div>
                <div className="flex flex-wrap gap-2">
                  {shops.length
                    ? shops.map(s => (
                        <CustomTag key={s.id} minimal>
                          {s.name}
                        </CustomTag>
                      ))
                    : "-"}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard
              label="Outstanding Orders"
              icon="clipboard"
              value={`${detail?.totalOutstandingOrders ?? 0} Orders`}
              sub="Unpaid"
            />
            <SummaryCard
              label="Gross Order Value"
              icon="document"
              tone="text-green"
              value={inr(detail?.grossOrderValue ?? 0)}
              sub={`${detail?.totalOutstandingOrders ?? 0} Invoices & Charges`}
            />
            <SummaryCard
              label="Total Outstanding Amount"
              icon="trending-up"
              tone="text-red"
              value={inr(detail?.totalOutstandingAmount ?? 0)}
              sub="Pay Amount"
            />
          </div>

          <div className="bg-white border rounded-lg">
            <div className="p-4 flex justify-between items-center border-b">
              <span className="font-600">Outstanding Listing</span>
              <CustomPopover
                position={Position.BOTTOM_RIGHT}
                minimal
                content={
                  <Menu title="Payment">
                    <MenuItem
                      text="Payment Settlement"
                      icon="bank-account"
                      disabled={!hasSelection}
                      onClick={() => setSettleOpen(true)}
                    />
                  </Menu>
                }
              >
                <CustomButton icon="menu" text="Actions" />
              </CustomPopover>
            </div>
            <MultipleOrderAddTransaction
              isOpen={settleOpen}
              onClose={() => setSettleOpen(false)}
              orders={picked}
              onDone={() => {
                resetSelected()
                refetch()
              }}
            />
            <CustomReactTable
              data={orders}
              columns={OUTSTANDING_COLUMNS}
              pageSize={Math.max(orders.length, 15)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorOutstandingOrders
